import { AdminOnly } from '@/components/admin-only'
import { createFileRoute, useNavigate, redirect } from '@tanstack/react-router'
import { useState } from 'react'
import { toast } from 'sonner'
import { AddressPicker, type AddressValue } from '@/components/address-picker'
import { scheduleToWorkflow } from '@/lib/schedule-to-workflow'
import { usePublishWorkflow } from '@/hooks/use-workflows'
import { Plus, Trash2, Pencil, Check, Truck, Upload, Mail, BellRing, AlertTriangle, Workflow } from 'lucide-react'
import { uploadLogo } from '@/lib/storage'
import { useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { SubscriptionCard } from '@/components/subscription-card'
import { useNotifications, useScheduleSends, useScheduleTrouble, useSendUsage, SMS_FREE_ALLOWANCE, SCHEDULE_STATUSES, conditionFor, statusFromCondition, type NotificationSchedule } from '@/hooks/use-notifications'
import { useRef } from 'react'
import { Page, PageHeader, Card, CardHeader, btnPrimary, btnSecondary, btnGhost, StatusPill, EmptyState, SkeletonRows, hoverActions, useConfirm } from '@/components/rm'
import { localeFor } from '@/lib/locale'
import { FEATURES } from '@/lib/features'
import { uploadPaymentQr } from '@/lib/storage'
import { UnsavedGuard } from '@/components/unsaved-guard'
import { Modal, Field, Input, Textarea, Select } from '@/components/rm/form'
import { useCompany, useTeam } from '@/hooks/use-company'
import { useAuth } from '@/hooks/use-auth'
import { useVehicles } from '@/hooks/use-vehicles'
import { hrcwList, jurisdictionFor, mapCategories, JURISDICTION_LABEL } from '@/lib/hrcw'
import { HrcwPicker } from '@/components/swms/hrcw-picker'
import { cn } from '@/lib/utils'
import { phFormat, TIME_ZONE, tidyPhone, peso, displayPhMobile } from '@/lib/ph'

// Grouped for the same reason the sidebar was: ten flat tabs meant reading all of them to find one.
// Price lists and email templates are deliberately absent — they are authored rather than
// configured, so they now live at /pricing and /templates. What remains genuinely is configuration.
const TAB_GROUPS = [
  { group: 'Business', tabs: [
    { id: 'business', label: 'Business profile' },
    { id: 'team', label: 'Team & vans' },
    { id: 'billing', label: 'Billing' },
  ] },
  { group: 'Work', tabs: [
    { id: 'ai', label: 'AI preferences' },
    { id: 'notifications', label: 'Notification schedules' },
    { id: 'booking', label: 'Online booking' },
    { id: 'safety', label: 'Safety & SWMS' },
  ] },
  { group: 'Connections', tabs: [
    { id: 'integrations', label: 'Integrations' },
    { id: 'sms', label: 'SMS' },
  ] },
] as const
type TabId = (typeof TAB_GROUPS)[number]['tabs'][number]['id']

// MySerbisyo (spec 001): tabs for features that are switched off are not shown, and a deep link to
// one falls back to the default tab. Safety holds SWMS; Integrations holds only Xero, QuickBooks,
// Reckon and Stripe, none of which MySerbisyo uses.
const TAB_ON: Partial<Record<TabId, boolean>> = {
  safety: FEATURES.swms,
  integrations: FEATURES.xero || FEATURES.stripePayments,
}
const tabOn = (id: TabId) => TAB_ON[id] !== false
const VISIBLE_GROUPS = TAB_GROUPS
  .map((g) => ({ group: g.group, tabs: g.tabs.filter((t) => tabOn(t.id)) }))
  .filter((g) => g.tabs.length > 0)
const TAB_IDS: readonly TabId[] = TAB_GROUPS.flatMap((g) => g.tabs.map((t) => t.id)).filter(tabOn)

// Where the two moved tabs went. These ids stayed valid deep-link targets rather than being deleted:
// the spec 009 onboarding checklist links to ?tab=pricing, and owners have bookmarks. Dropping them
// would have sent people to a silently wrong tab — the same shape of failure as a stale ON CONFLICT.
const MOVED: Record<string, string> = { pricing: '/pricing', templates: '/templates' }

// Statuses that can trigger a review request. Deliberately not the full JOB_STATUSES list:
// 'Invoiced' and 'Paid' mean money moved, which is not evidence the work is finished, and asking
// for a review off the back of a payment is how you end up reviewing a job that went badly.
const REVIEW_TRIGGER_STATUSES = ['Completed', 'Completed-Follow-Up', 'Closed'] as const

// Deep-link support (spec 009 T001): `/settings?tab=notifications` opens that tab. Unknown or
// missing values are dropped so the page falls back to the default rather than 404-ing on search.
export const Route = createFileRoute('/_app/settings')({
  validateSearch: (s: Record<string, unknown>): { tab?: TabId | keyof typeof MOVED } => {
    const t = String(s.tab ?? '')
    if (t in MOVED) return { tab: t as keyof typeof MOVED }
    return TAB_IDS.some((id) => id === t) ? { tab: t as TabId } : {}
  },
  beforeLoad: ({ search }) => {
    const to = search.tab && MOVED[search.tab]
    if (to) throw redirect({ to, replace: true })
  },
  component: () => <AdminOnly><SettingsPage /></AdminOnly>,
})

function SettingsPage() {
  const navigate = useNavigate()
  const { tab: urlTab } = Route.useSearch()
  const [tab, setTab] = useState<TabId>((urlTab as TabId) ?? 'business')
  // Keep the URL in step with the active tab so a deep link is shareable and back/refresh land
  // on the same tab. `replace` avoids stacking a history entry per tab click.
  const applyTab = (next: TabId) => {
    setTab(next)
    navigate({ to: '/settings', search: { tab: next }, replace: true })
  }
  // Unsaved-changes tracking (spec 005). Tabs that hold editable state report up via
  // onDirty; a tab switch discards those edits exactly like a route change would, so it
  // goes through the same confirm. UnsavedGuard covers leaving /settings altogether.
  const [dirty, setDirty] = useState(false)
  const [pendingTab, setPendingTab] = useState<TabId | null>(null)

  const requestTab = (next: TabId) => {
    if (next === tab) return
    if (dirty) return setPendingTab(next)
    applyTab(next)
  }
  const discardAndSwitch = () => {
    if (!pendingTab) return
    setDirty(false)
    applyTab(pendingTab)
    setPendingTab(null)
  }

  return (
    <Page>
      <UnsavedGuard when={dirty} />
      <PageHeader title="Settings" subtitle="Business, team, automation and account configuration." />
      <Modal
        open={!!pendingTab}
        onClose={() => setPendingTab(null)}
        title="Discard unsaved changes?"
        footer={
          <>
            <button className={btnSecondary} onClick={() => setPendingTab(null)}>Keep editing</button>
            <button className={btnPrimary} onClick={discardAndSwitch}>Discard changes</button>
          </>
        }
      >
        <p className="text-[13.5px] leading-relaxed text-token-secondary">
          You have edits on this tab that haven&apos;t been saved. If you switch now they&apos;ll be lost.
        </p>
      </Modal>
      <div className="mt-5 flex flex-col gap-5 lg:flex-row lg:gap-8">
        {/* Sub-nav */}
        {/* Headings show on desktop only; on mobile this is a horizontal strip where group labels
            would cost more room than they earn. */}
        <nav className="flex gap-1 overflow-x-auto pb-1 lg:w-[190px] lg:flex-none lg:flex-col lg:gap-0 lg:overflow-visible lg:pb-0 lg:pt-1">
          {VISIBLE_GROUPS.map(({ group, tabs }) => (
            <div key={group} className="flex flex-none gap-1 lg:mt-3.5 lg:block lg:first:mt-0">
              <p className="hidden px-3 pb-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-token-muted lg:block">
                {group}
              </p>
              <div className="flex gap-1 lg:flex-col lg:space-y-0.5">
                {tabs.map((t) => {
                  const on = t.id === tab
                  return (
                    <button
                      key={t.id}
                      onClick={() => requestTab(t.id)}
                      className={cn(
                        'flex-none whitespace-nowrap rounded-[9px] px-3 py-2 text-left text-[13.5px] font-semibold transition-colors lg:block lg:w-full',
                        on ? 'bg-token-selected text-brand' : 'text-token-secondary hover:bg-token-hover hover:text-token-primary',
                      )}
                    >
                      {t.label}
                    </button>
                  )
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* Content */}
        <div className="min-w-0 flex-1">
          {tab === 'business' && <BusinessTab onDirty={setDirty} />}
          {tab === 'team' && <TeamTab />}
          {tab === 'ai' && <AiPreferencesTab />}
          {tab === 'notifications' && <SchedulesTab />}
          {tab === 'billing' && <BillingTab />}
          {tab === 'sms' && <SmsTab />}
          {tab === 'booking' && <BookingTab />}
          {tab === 'safety' && FEATURES.swms && <SafetyTab />}
          {tab === 'integrations' && (FEATURES.xero || FEATURES.stripePayments) && <IntegrationsTab />}
        </div>
      </div>
    </Page>
  )
}

function SectionHead({ title, sub }: { title: string; sub: string }) {
  return (
    <div className="mb-5">
      <h2 className="font-display text-[20px] font-bold tracking-[-0.01em] text-token-primary">{title}</h2>
      <p className="mt-0.5 text-[13.5px] text-token-secondary">{sub}</p>
    </div>
  )
}

function AiPreferencesTab() {
  const { company, save } = useCompany()
  return (
    <>
      <SectionHead title="AI preferences" sub="Tune how MySerbisyo AI drafts your quotes." />
      <Card className="max-w-2xl">
        <CardHeader title="Quoting" />
        <div className="grid gap-4 p-5 sm:grid-cols-2">
          <Field label="Target margin (%)"><Input type="number" defaultValue={35} /></Field>
          <Field label="Quote tone">
            <Select defaultValue="professional">
              <option value="professional">Professional</option>
              <option value="friendly">Friendly</option>
              <option value="concise">Concise</option>
            </Select>
          </Field>
          <Field label="Ground on" className="sm:col-span-2">
            <Select defaultValue="both">
              <option value="both">Price list + accepted history</option>
              <option value="catalogue">Price list only</option>
            </Select>
          </Field>
          <label className="flex items-center gap-2.5 sm:col-span-2">
            <input type="checkbox" defaultChecked className="h-4 w-4 accent-brand" />
            <span className="text-[13.5px] text-token-primary">Auto-draft a quote when a new enquiry is created</span>
          </label>
        </div>
        <div className="flex justify-end border-t border-token-subtle px-5 py-3.5">
          <button className={btnPrimary} onClick={() => company && save.mutate({})}>Save preferences</button>
        </div>
      </Card>

      <EmailToQuoteCard />
    </>
  )
}

function EmailToQuoteCard() {
  const { company, save } = useCompany()
  const address = company?.inbound_token ? `q-${company.inbound_token}@myserbisyo.net` : ''
  const enabled = company?.inbound_quote_enabled ?? true
  const copy = () => { if (address) { navigator.clipboard.writeText(address); toast.success('Address copied') } }

  const included = company?.ai_quote_included ?? 0
  const used = company?.ai_quote_used ?? 0
  const topups = company?.ai_quote_topups ?? 0
  const remaining = Math.max(0, included - used) + topups
  const [buying, setBuying] = useState(false)
  const [aiPack, setAiPack] = useState<'small' | 'medium' | 'large'>('large')
  // ServicePilot's AUD amounts, hidden while FEATURES.stripePayments is off. Set peso prices here and
  // in ai-quote-checkout before turning it on.
  const AIPACKS = { small: { n: '25', price: 12 }, medium: { n: '100', price: 39 }, large: { n: '300', price: 99 } } as const
  const aiPackBtn = 'flex flex-col items-start rounded-[14px] border p-4 text-left transition hover:shadow-sm'
  const topUp = async () => {
    setBuying(true)
    const { data, error } = await supabase.functions.invoke('ai-quote-checkout', { body: { pack: aiPack } })
    setBuying(false)
    if (error || !(data as { url?: string })?.url) return toast.error('Could not start checkout')
    window.location.href = (data as { url: string }).url
  }
  return (
    <Card className="mt-5 max-w-2xl">
      <CardHeader title="Email to quote" action={
        <label className="flex items-center gap-2 text-[13px] font-semibold text-token-secondary">
          <input type="checkbox" checked={enabled} disabled={!company} onChange={(e) => save.mutate({ inbound_quote_enabled: e.target.checked })} className="h-4 w-4 accent-brand" />
          {enabled ? 'On' : 'Off'}
        </label>
      } />
      <div className="space-y-4 p-5">
        <p className="text-[13.5px] leading-relaxed text-token-secondary">
          Forward any customer enquiry to your private address and MySerbisyo reads it, matches your price list,
          and creates a <strong className="text-token-primary">draft quote</strong> for you to review — nothing is sent to the customer automatically.
        </p>
        <div className="flex items-center gap-2 rounded-[10px] border border-token-subtle bg-token-warning-surface px-3 py-2.5">
          <Mail className="h-4 w-4 flex-none text-token-primary" />
          <code className="flex-1 truncate text-[13px] font-semibold text-token-primary">{address || '—'}</code>
          <button onClick={copy} disabled={!address} className="inline-flex items-center gap-1 rounded-[7px] bg-brand px-2.5 py-1 text-[12px] font-bold text-brand-fg transition-colors hover:bg-brand-hover disabled:opacity-50">
            <Check className="h-3.5 w-3.5" /> Copy
          </button>
        </div>
        <div className="rounded-[10px] border border-token-subtle px-3 py-2.5">
          <div className="text-[13px] font-semibold text-token-primary">{remaining} AI quote {remaining === 1 ? 'draft' : 'drafts'} left</div>
          <div className="text-[11.5px] text-token-secondary">{included}/mo included{topups > 0 ? ` · ${topups} top-up` : ''} · resets monthly</div>
        </div>
        {/* Bought by card through Stripe, which MySerbisyo does not use (spec 001). */}
        {FEATURES.stripePayments && (
        <div>
          <p className="mb-2 text-[13px] font-bold text-token-primary">Top up</p>
          <div className="grid gap-3 sm:grid-cols-3" role="radiogroup" aria-label="Choose an AI quote pack">
            {(['small', 'medium', 'large'] as const).map((k) => (
              <button
                key={k}
                role="radio"
                aria-checked={aiPack === k}
                onClick={() => setAiPack(k)}
                className={cn(aiPackBtn, 'relative', aiPack === k ? 'border-token-strong bg-token-warning-surface ring-2 ring-accent' : 'border-token-subtle hover:border-token-subtle')}
              >
                {aiPack === k && <span className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-brand text-[11px] font-bold text-accent">✓</span>}
                <span className={cn('text-[12px] font-bold uppercase', k === 'large' ? 'text-token-warning' : 'text-token-secondary')}>{AIPACKS[k].n} drafts{k === 'large' ? ' · best value' : ''}</span>
                <span className="font-display text-[22px] font-bold text-token-primary">{peso(AIPACKS[k].price, { whole: true })}</span>
                <span className="text-[12px] text-token-secondary">{(AIPACKS[k].price * 100 / Number(AIPACKS[k].n)).toFixed(0)}¢ each</span>
              </button>
            ))}
          </div>
          <button className={cn(btnPrimary, 'mt-4 w-full justify-center sm:w-auto')} onClick={topUp} disabled={buying}>
            {buying ? 'Opening secure checkout…' : `Buy ${AIPACKS[aiPack].n} AI drafts for ${peso(AIPACKS[aiPack].price, { whole: true })}`}
          </button>
          <p className="mt-2 text-[12px] text-token-secondary">Secure card payment via Stripe. Drafts are added to your balance automatically after payment and never expire.</p>
        </div>
        )}
        <p className="text-[12.5px] text-token-secondary">
          Tip: set an auto-forward rule in your business inbox (Gmail/Outlook) so enquiries flow in automatically. Each
          draft appears under Quotes and you’ll get a notification. Out of credits? Enquiries are still saved as drafts —
          just without automatic pricing.
        </p>
      </div>
    </Card>
  )
}

/* ─────────────────────────── Business & branding ─────────────────────────── */
function BusinessTab({ onDirty }: { onDirty: (d: boolean) => void }) {
  const { company, isLoading, save } = useCompany()
  const { templates } = useNotifications()
  const { data: usage } = useSendUsage()
  const [form, setForm] = useState<Record<string, string>>({})
  const [accent, setAccent] = useState('')
  const [logo, setLogo] = useState('')
  const [uploading, setUploading] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [reviewUrl, setReviewUrl] = useState('')
  const [reviewEnabled, setReviewEnabled] = useState(true)
  const [reviewChannel, setReviewChannel] = useState('both')
  const [reviewTemplateId, setReviewTemplateId] = useState<number | null>(null)
  // Which job statuses ask for a review, and how long to wait after the status changes (035).
  const [reviewStatuses, setReviewStatuses] = useState<string[]>(['Completed'])
  const [reviewDelayDays, setReviewDelayDays] = useState(0)
  const [testing, setTesting] = useState(false)

  // Save first, then send: testing the wording you have not saved yet tests the wrong wording.
  const sendTestReview = async () => {
    setTesting(true)
    try {
      await save.mutateAsync({
        google_review_url: reviewUrl || null, review_request_enabled: reviewEnabled,
        review_channel: reviewChannel, review_request_template_id: reviewTemplateId,
        review_request_statuses: reviewStatuses, review_request_delay_days: reviewDelayDays,
      })
      savedReviews.current = reviewsSig
      const { data, error } = await supabase.functions.invoke('send-test-review')
      if (error) throw error
      const d = data as { sent_to?: string; using?: string; error?: string }
      if (d?.error) throw new Error(d.error)
      toast.success(`Test sent to ${d.sent_to} using ${d.using}`)
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Could not send the test') }
    finally { setTesting(false) }
  }

  // Unsaved-changes tracking (spec 005). Two independent signatures because the card has two
  // Save buttons — saving reviews must not clear a pending profile edit, or vice versa.
  // The logo is excluded: it persists the moment it uploads, so it is never an unsaved edit.
  const savedProfile = useRef('')
  const savedReviews = useRef('')
  const sigOf = (v: unknown) => JSON.stringify(v)

  if (company && !loaded) {
    setLoaded(true)
    const seed = {
      name: company.name ?? '', abn: company.abn ?? '', email: company.email ?? '', phone: company.phone ?? '',
      address: company.address ?? '', city: company.city ?? '', state: company.state ?? '', postal_code: company.postal_code ?? '',
      country: company.country ?? 'Philippines',
      default_tax_rate: String(company.default_tax_rate ?? 10),
    }
    const seedAccent = company.accent_color ?? '#0038a8'
    const seedReviews = {
      url: company.google_review_url ?? '',
      enabled: company.review_request_enabled ?? true,
      channel: company.review_channel ?? 'both',
      templateId: company.review_request_template_id ?? null,
      statuses: company.review_request_statuses ?? ['Completed'],
      delayDays: company.review_request_delay_days ?? 0,
    }
    setForm(seed)
    setAccent(seedAccent)
    setLogo(company.logo ?? '')
    setReviewUrl(seedReviews.url)
    setReviewEnabled(seedReviews.enabled)
    setReviewChannel(seedReviews.channel)
    setReviewTemplateId(seedReviews.templateId)
    setReviewStatuses(seedReviews.statuses)
    setReviewDelayDays(seedReviews.delayDays)
    savedProfile.current = sigOf({ form: seed, accent: seedAccent })
    savedReviews.current = sigOf(seedReviews)
  }

  const profileSig = sigOf({ form, accent })
  const reviewsSig = sigOf({ url: reviewUrl, enabled: reviewEnabled, channel: reviewChannel, templateId: reviewTemplateId, statuses: reviewStatuses, delayDays: reviewDelayDays })
  const isDirty = loaded && (profileSig !== savedProfile.current || reviewsSig !== savedReviews.current)

  useEffect(() => {
    onDirty(isDirty)
    return () => onDirty(false)
  }, [isDirty, onDirty])

  const saveReviews = async () => {
    try {
      await save.mutateAsync({
        google_review_url: reviewUrl || null, review_request_enabled: reviewEnabled,
        review_channel: reviewChannel, review_request_template_id: reviewTemplateId,
        review_request_statuses: reviewStatuses, review_request_delay_days: reviewDelayDays,
      })
      savedReviews.current = reviewsSig
      toast.success('Review settings saved')
    }
    catch (e) { toast.error(e instanceof Error ? e.message : 'Failed') }
  }
  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }))
  const [addrManual, setAddrManual] = useState(false)

  const onLogo = async (file: File) => {
    if (!company) return
    if (file.size > 2_000_000) return toast.error('Logo must be under 2 MB.')
    setUploading(true)
    try {
      const url = await uploadLogo(file, company.id)
      setLogo(url)
      await save.mutateAsync({ logo: url })
      toast.success('Logo updated')
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Upload failed') }
    finally { setUploading(false) }
  }

  // MySerbisyo (spec 001): Philippines only, so the labels never change.
  const loc = localeFor()

  const submit = async () => {
    try {
      await save.mutateAsync({
        name: form.name, abn: form.abn, phone: tidyPhone(form.phone as string | null),
        default_tax_rate: Number(form.default_tax_rate ?? 12),
        address: form.address, city: form.city, state: form.state, postal_code: form.postal_code,
        accent_color: accent, logo: logo || null,
      })
      savedProfile.current = profileSig
      toast.success('Business profile saved')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed')
    }
  }

  if (isLoading) return <Card><SkeletonRows /></Card>
  return (
    <div className="grid gap-5 lg:grid-cols-3">
      <Card className="lg:col-span-2">
        <CardHeader title="Business profile" />
        <div className="grid gap-4 p-5 sm:grid-cols-2">
          <Field label="Business name"><Input value={form.name ?? ''} onChange={(e) => set('name', e.target.value)} /></Field>
          {/* The TIN is printed on every quote and invoice the trade person sends (BIR). */}
          <Field label={loc.businessNumberLabel}>
            <Input value={form.abn ?? ''} onChange={(e) => set('abn', e.target.value)} placeholder={loc.businessNumberPlaceholder} />
          </Field>
          {/* Read-only on purpose. This is the ACCOUNT email, not a display field: it matches the
              owner's login on every live tenant, it is the Stripe billing customer, it is the
              reply-to on quote and invoice chasers, and it is where inbound enquiry notifications
              land. Editing it here changed all of that silently and left the login behind. */}
          <Field label="Email">
            <Input value={form.email ?? ''} readOnly disabled type="email" className="bg-token-surface text-token-secondary" />
            <span className="mt-1 block text-[12px] leading-relaxed text-token-secondary">
              This is your account email. It is also your sign-in address and your billing contact.
              To change it, contact support.
            </span>
          </Field>
          <Field label="Phone"><Input value={form.phone ?? ''} onChange={(e) => set('phone', e.target.value)} /></Field>
          <AddressPicker
            className="sm:col-span-2"
            value={{
              address: form.address ?? null, line1: form.line1 ?? null, line2: form.line2 ?? null,
              city: form.city ?? null, state: form.state ?? null, postal_code: form.postal_code ?? null,
              manual_address: addrManual,
            }}
            onChange={(a: AddressValue) => {
              set('address', a.address ?? ''); set('line1', a.line1 ?? ''); set('line2', a.line2 ?? '')
              set('city', a.city ?? ''); set('state', a.state ?? ''); set('postal_code', a.postal_code ?? '')
              // `form` is Record<string,string>, so the manual toggle lives in local state rather
              // than being coerced to "true"/"false" into a boolean column.
              setAddrManual(a.manual_address)
            }}
          />
        </div>
        <div className="flex justify-end border-t border-token-subtle px-5 py-3.5">
          <button className={btnPrimary} onClick={submit} disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save profile'}</button>
        </div>
      </Card>

      <Card>
        <CardHeader title="Branding" />
        <div className="space-y-4 p-5">
          <Field label="Logo">
            {logo ? (
              <div className="flex items-center gap-3">
                <img src={logo} alt="Logo" className="h-14 w-14 rounded-[10px] border border-token-subtle bg-token-surface object-contain p-1" />
                <label className="cursor-pointer text-[13px] font-semibold text-token-primary hover:text-token-primary">
                  {uploading ? 'Uploading…' : 'Replace'}
                  <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={(e) => e.target.files?.[0] && onLogo(e.target.files[0])} />
                </label>
                <button className="text-[13px] font-semibold text-token-muted hover:text-token-danger" onClick={() => { setLogo(''); save.mutate({ logo: null }) }}>Remove</button>
              </div>
            ) : (
              <label className="flex cursor-pointer items-center justify-center gap-2 rounded-[10px] border border-dashed border-token-subtle px-3 py-3 text-[13px] font-semibold text-token-secondary hover:border-token-strong hover:text-token-primary">
                <Upload className="h-4 w-4" /> {uploading ? 'Uploading…' : 'Upload logo'}
                <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={(e) => e.target.files?.[0] && onLogo(e.target.files[0])} />
              </label>
            )}
            <p className="mt-1 text-[12px] text-token-secondary">PNG/JPG/SVG, under 2 MB. Shown on quotes, invoices and emails.</p>
          </Field>
          <Field label="Accent colour">
            <div className="flex items-center gap-3">
              <input type="color" value={accent || '#0038a8'} onChange={(e) => setAccent(e.target.value)} className="h-9 w-12 cursor-pointer rounded-[8px] border border-token-subtle bg-token-surface p-1" />
              <Input value={accent} onChange={(e) => setAccent(e.target.value)} className="font-mono" />
            </div>
          </Field>
          <p className="text-[12.5px] text-token-secondary">Applied to quote &amp; invoice PDFs and hosted customer pages.</p>
          <div className="rounded-[12px] border border-token-subtle p-4">
            <div className="flex items-center gap-2.5">
              {logo
                ? <img src={logo} alt="" className="h-8 w-8 rounded-[9px] border border-token-subtle bg-token-surface object-contain p-0.5" />
                : <span className="flex h-8 w-8 items-center justify-center rounded-[9px] text-[15px] font-bold text-white" style={{ background: accent || '#0038a8' }}>◈</span>}
              <span className="font-display text-[16px] font-bold tracking-[-0.02em] text-token-primary">{form.name || 'Your business'}</span>
            </div>
            <button className="mt-3 w-full rounded-[10px] py-2 text-[13px] font-bold text-white" style={{ background: accent || '#0038a8' }}>Accept quote</button>
          </div>
        </div>
      </Card>

      <Card className="lg:col-span-3">
        <CardHeader title="Review requests" />
        <div className="space-y-4 p-5">
          <label className="flex items-start gap-2.5">
            <input type="checkbox" checked={reviewEnabled} onChange={(e) => setReviewEnabled(e.target.checked)} className="mt-0.5 h-4 w-4 accent-brand" />
            <span className="text-[13.5px] text-token-primary">Automatically ask customers for a review after a job is completed<span className="block text-[12px] text-token-secondary">Asks once per job, and only for jobs you finish <b>after</b> switching this on. Work you completed before today is never asked, however you edit it later.</span></span>
          </label>
          <Field label="Google review link">
            <Input value={reviewUrl} onChange={(e) => setReviewUrl(e.target.value)} placeholder="https://g.page/r/…/review" />
          </Field>
          <Field label="Send review requests by">
            <Select value={reviewChannel} onChange={(e) => setReviewChannel(e.target.value)}>
              <option value="both">SMS &amp; email (recommended)</option>
              <option value="sms">SMS only</option>
              <option value="email">Email only</option>
            </Select>
          </Field>

          {/* Which statuses count as "finished" is the business's call, not ours. A cleaner whose
              work ends with a follow-up visit wants the customer asked once, after that visit —
              asking at Completed as well means two emails for one job. */}
          <Field label="Ask when a job reaches">
            <div className="flex flex-wrap gap-1.5">
              {REVIEW_TRIGGER_STATUSES.map((s) => {
                const on = reviewStatuses.includes(s)
                return (
                  <button
                    key={s} type="button"
                    onClick={() => setReviewStatuses(on ? reviewStatuses.filter((x) => x !== s) : [...reviewStatuses, s])}
                    className={cn('rounded-full border px-3 py-1.5 text-[13px] font-medium transition-colors',
                      on ? 'border-token-strong bg-brand text-brand-fg' : 'border-token-subtle bg-token-surface text-token-secondary hover:bg-canvas-subtle')}
                  >
                    {s}
                  </button>
                )
              })}
            </div>
            {reviewStatuses.length === 0 && (
              <p className="mt-1.5 text-[12px] font-semibold text-token-danger">
                Nothing selected, so no review requests will be sent.
              </p>
            )}
          </Field>

          <Field label="Wait before asking">
            <Select value={String(reviewDelayDays)} onChange={(e) => setReviewDelayDays(Number(e.target.value))}>
              <option value="0">Straight away</option>
              {[1, 2, 3, 5, 7, 14].map((d) => (
                <option key={d} value={d}>{d} day{d === 1 ? '' : 's'} after</option>
              ))}
            </Select>
            <p className="mt-1.5 text-[12px] text-token-secondary">
              Counted from the moment the job reaches {reviewStatuses.length === 1 ? reviewStatuses[0] : 'one of those statuses'},
              not from the job&apos;s dates. {reviewDelayDays === 0
                ? 'Straight away means about two hours, so a job that gets reopened by mistake is never asked about.'
                : 'If the job is reopened before then, nothing is sent.'}
            </p>
          </Field>
          {/* Her own wording, previewable, in the editor she already knows. The default quoted the
              job name back to the customer, and one tenant keeps internal notes in that field — so
              "keys onsite ripley" went out to 198 people. No template can reach job free-text now. */}
          <Field label="Email wording">
            <Select value={String(reviewTemplateId ?? '')} onChange={(e) => setReviewTemplateId(e.target.value ? Number(e.target.value) : null)}>
              <option value="">Built-in default</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name || t.subject}</option>)}
            </Select>
          </Field>
          <p className="text-[12px] text-token-secondary">
            Write your own subject and wording in <b>Email templates</b> (with a preview), then pick it here.
            Your words go inside your branded email, and we always add the review button, so you can’t
            accidentally send one without a link. Merge fields:
            {' '}<code>[[contact.first_name]]</code>, <code>[[company.name]]</code>, <code>[[review_url]]</code>.
          </p>
          {/* Read it before a customer does. Not being able to was the whole problem: the wording
              quoted an internal notes field, and 198 people saw it before anyone here did. */}
          <div className="flex items-center gap-3">
            <button className={btnSecondary} onClick={sendTestReview} disabled={testing}>
              {testing ? 'Sending…' : 'Send me a test'}
            </button>
            <span className="text-[12px] text-token-secondary">Goes to your own address only.</span>
          </div>
          <p className="text-[12px] text-token-secondary">Tip: in Google Business Profile → “Ask for reviews”, copy your short review link and paste it here. Without a link, no requests are sent.</p>

          {/* Usage, because a runaway automation spends real money and allowance. Nothing counted
              email until 198 went out in 21 seconds and had to be reconstructed from SES. */}
          {usage && (
            <div className="rounded-[10px] border border-token-subtle bg-token-surface px-3.5 py-3 text-[12.5px]">
              <p className="font-semibold text-token-primary">This month</p>
              <p className="mt-1 text-token-secondary">
                Emails <b className="tabular-nums">{usage.emails_this_month}</b> of{' '}
                <span className="tabular-nums">{usage.email_limit}</span>
                {' · '}
                Free SMS <b className="tabular-nums">{usage.sms_free_this_month}</b> of{' '}
                <span className="tabular-nums">{SMS_FREE_ALLOWANCE}</span>
                {usage.sms_credits > 0 && <> {' · '}<span className="tabular-nums">{usage.sms_credits}</span> SMS credits</>}
              </p>
              {usage.emails_this_month >= usage.email_limit && (
                <p className="mt-1 font-semibold text-token-danger">Email limit reached — automated emails are paused until next month.</p>
              )}
            </div>
          )}
          <div className="flex justify-end">
            <button className={btnPrimary} onClick={saveReviews} disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save review settings'}</button>
          </div>
        </div>
      </Card>
    </div>
  )
}


/* ─────────────────────────── Team & vehicles ─────────────────────────── */
function TeamTab() {
  const { members, isLoading } = useTeam()
  const { company, save } = useCompany()
  const { vehicles, create: createVehicle, remove: removeVehicle } = useVehicles()
  const [veh, setVeh] = useState<{ model: string; license_plate: string } | null>(null)
  const qc = useQueryClient()
  const [inv, setInv] = useState<{ email: string; role: string } | null>(null)
  const [inviteBusy, setInviteBusy] = useState(false)
  const pending = useQuery({
    queryKey: ['team-invites'],
    queryFn: async () => (await supabase.from('invitation').select('id, email, role, status').eq('status', 'pending').order('created_at', { ascending: false })).data as Array<{ id: string; email: string; role: string }> | null,
  })
  const seats = company?.max_users ?? 0
  const used = members.length + (pending.data?.length ?? 0)
  const subscribed = !!company?.stripe_subscription_id
  const seatsFull = seats > 0 && used >= seats && !subscribed
  const { user } = useAuth()
  const sendInvite = async (addSeat = false) => {
    if (!inv) return
    setInviteBusy(true)
    const { data, error } = await supabase.functions.invoke('invite-create', { body: { email: inv.email, role: inv.role, addSeat } })
    setInviteBusy(false)
    if (error || !(data as { ok?: boolean })?.ok) {
      const ctx = (error as { context?: Response } | null)?.context
      let msg = 'Could not send invite'
      let code: string | null = null
      try { if (ctx) { const j = await ctx.json(); if (j?.error) msg = j.error; code = j?.code ?? null } } catch { /* */ }
      if (code === 'seats_full' && subscribed && !addSeat) {
        if (await confirm({
          title: 'Add another seat?',
          body: `You're using all ${seats} seat${seats === 1 ? '' : 's'}. Adding one is prorated on your next invoice.`,
          confirmLabel: 'Add a seat and invite',
        })) {
          return sendInvite(true)
        }
        return
      }
      return toast.error(msg)
    }
    toast.success((data as { emailed?: boolean }).emailed ? 'Invite emailed' : 'Invite created')
    setInv(null); qc.invalidateQueries({ queryKey: ['team-invites'] })
  }
  // Spec 038. Role lives in the JWT as well as this table, so it can only be changed server-side —
  // see supabase/functions/role-set. The list is refetched from the server rather than patched
  // locally, so what the UI shows is what actually saved.
  const [confirm, confirmDialog] = useConfirm()
  const [roleBusy, setRoleBusy] = useState<string | null>(null)
  const adminCount = members.filter((m) => m.role === 'company-admin').length
  const iAmAdmin = members.find((m) => m.id === user?.id)?.role === 'company-admin'
  const setRole = async (m: { id: string; role: string | null; first_name: string | null; email: string | null }, role: string) => {
    if (role === (m.role ?? 'user')) return
    const who = m.first_name || m.email || 'this member'
    const toAdmin = role === 'company-admin'
    if (!(await confirm({
      title: toAdmin ? `Make ${who} an administrator?` : `Change ${who} to a team member?`,
      body: toAdmin
        ? 'They will be able to see and change everything on the account, including billing and the team.'
        : "They lose access to settings, billing and other people's jobs.",
      confirmLabel: toAdmin ? 'Make administrator' : 'Change to team member',
      tone: toAdmin ? 'default' : 'danger',
    }))) return
    setRoleBusy(m.id)
    const { data, error } = await supabase.functions.invoke('role-set', { body: { userId: m.id, role } })
    setRoleBusy(null)
    if (error || !(data as { ok?: boolean })?.ok) {
      const ctx = (error as { context?: Response } | null)?.context
      let msg = 'Could not change the role'
      try { if (ctx) { const j = await ctx.json(); if (j?.error) msg = j.error } } catch { /* */ }
      return toast.error(msg)
    }
    // Claims are baked into an access token when it is issued, so a session already in someone's
    // pocket keeps the old role until it refreshes. Say so rather than let them think it failed.
    toast.success(
      m.id === user?.id
        ? 'Your role changed. Sign out and back in for it to take effect.'
        : `${who} is now ${toAdmin ? 'an administrator' : 'a team member'}. It applies when they next sign in.`
    )
    qc.invalidateQueries({ queryKey: ['team'] })
  }
  const removeMember = async (m: { id: string; email: string | null; first_name: string | null }) => {
    const who = m.first_name || m.email || 'this member'
    if (!(await confirm({
      title: `Remove ${who} from the team?`,
      body: `They lose access immediately.${subscribed ? ' The seat stays on your plan; reduce seats in Settings → Billing if you no longer need it.' : ''}`,
      confirmLabel: 'Remove',
      tone: 'danger',
    }))) return
    const { error } = await supabase.from('users').update({ deleted_at: new Date().toISOString() }).eq('id', m.id)
    if (error) return toast.error('Could not remove member')
    if (subscribed) supabase.functions.invoke('seat-sync', {}).catch(() => {})
    toast.success(`${who} removed`)
    qc.invalidateQueries({ queryKey: ['team'] })
  }

  // Team and Job visibility run the full width. Both have wide content — the member row carries
  // a payroll ID, an hourly rate, a role pill and Remove — and at half width the row was
  // squeezing and the visibility copy was reading as a narrow column. Vehicles is short enough
  // to stay in the two-column flow.
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      {confirmDialog}
      <Card className="lg:col-span-2">
        <CardHeader title="Team" count={members.length} action={<button className={btnGhost} disabled={seatsFull} onClick={() => setInv({ email: '', role: 'user' })}><Plus className="h-4 w-4" /> Invite</button>} />
        {isLoading ? <SkeletonRows /> : (members.length === 0 && (pending.data?.length ?? 0) === 0) ? (
          <EmptyState label="No team members yet. Invite your crew to get started." cta="Invite someone" onCta={() => setInv({ email: '', role: 'user' })} />
        ) : (
          <ul className="divide-y divide-token-subtle">
            {/* min-w-0 all the way down, or the name column refuses to shrink, pushes the row
                wider than the card, and the role pill gets clipped off the right edge. The
                controls stay flex-none so they are never the thing that gets squeezed. */}
            {members.map((m) => (
              <li key={m.id} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-5 py-3">
                <div className="flex min-w-0 flex-1 basis-[240px] items-center gap-3">
                  <span className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-token-selected text-[13px] font-bold text-token-warning">
                    {(m.first_name?.[0] ?? m.email?.[0] ?? '?').toUpperCase()}
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-[14px] font-semibold text-token-primary">{[m.first_name, m.last_name].filter(Boolean).join(' ') || m.email || (m.phone ? displayPhMobile(m.phone) : '')}</p>
                    {/* Long addresses were breaking mid-word across two lines and stretching the
                        row. One line, ellipsised, with the full address on hover. */}
                    <p className="truncate text-[12px] text-token-secondary" title={m.email ?? ''}>{m.email || (m.phone ? displayPhMobile(m.phone) : '')}</p>
                  </div>
                </div>
                <div className="flex flex-none items-center gap-3">
                  {/* Spec 023 — matches this person to their External ID in Employment Hero /
                      KeyPay. Without it a payroll import cannot tell whose hours these are. */}
                  {FEATURES.payrollExport && (
                  <label className="flex items-center gap-1 text-[12px] text-token-secondary">
                    <input defaultValue={m.payroll_id ?? ''} placeholder="Payroll ID"
                      onBlur={async (e) => {
                        const v = e.target.value.trim() || null
                        await supabase.from('users').update({ payroll_id: v }).eq('id', m.id)
                        toast.success('Payroll ID saved')
                      }}
                      className="h-8 w-[110px] rounded-[8px] border border-token-subtle px-2 text-[13px] outline-none focus:border-token-strong" />
                  </label>
                  )}
                  <label className="flex items-center gap-1 text-[12px] text-token-secondary">₱
                    <input type="number" min={0} step={5} defaultValue={m.hourly_cost ?? ''} placeholder="—"
                      onBlur={async (e) => {
                        const v = e.target.value === '' ? null : Number(e.target.value)
                        await supabase.from('users').update({ hourly_cost: v }).eq('id', m.id)
                        toast.success('Hourly cost saved')
                      }}
                      className="h-8 w-[74px] rounded-[8px] border border-token-subtle px-2 text-right text-[13px] outline-none focus:border-token-strong" />
                    /hr
                  </label>
                  {/* Only an admin can change roles, and the last remaining admin cannot be
                      demoted — the server enforces both; this just stops the UI offering a
                      choice that is going to be refused. */}
                  {iAmAdmin ? (
                    <select
                      value={m.role === 'company-admin' ? 'company-admin' : 'user'}
                      disabled={roleBusy === m.id || (m.role === 'company-admin' && adminCount <= 1)}
                      title={m.role === 'company-admin' && adminCount <= 1 ? 'This is the only administrator. Make someone else an administrator first.' : 'Change role'}
                      onChange={(e) => setRole(m, e.target.value)}
                      className="h-8 flex-none rounded-[8px] border border-token-subtle bg-token-surface px-2 text-[13px] outline-none focus:border-token-strong disabled:cursor-not-allowed disabled:bg-token-surface disabled:text-token-muted"
                    >
                      <option value="user">Team member</option>
                      <option value="company-admin">Admin</option>
                    </select>
                  ) : (
                    <span className="flex-none"><StatusPill status={m.role ?? 'tech'} label={m.role ?? 'tech'} /></span>
                  )}
                  {m.id !== user?.id && (
                    <button className="text-[12px] font-semibold text-token-muted transition-colors hover:text-token-danger"
                      onClick={() => removeMember(m)}>Remove</button>
                  )}
                </div>
              </li>
            ))}
            {(pending.data ?? []).map((p) => (
              <li key={p.id} className="flex items-center justify-between px-5 py-3">
                <div className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-canvas-subtle text-[13px] font-bold text-token-secondary">{(p.email[0] ?? '?').toUpperCase()}</span>
                  <div>
                    <p className="text-[14px] font-semibold text-token-primary">{p.email}</p>
                    <p className="text-[12px] text-token-secondary">Invited · {p.role}</p>
                  </div>
                </div>
                <StatusPill status="pending" label="Pending" />
              </li>
            ))}
          </ul>
        )}
        <div className="border-t border-token-subtle px-5 py-3 text-[12.5px] text-token-secondary">
          {seats > 0 ? `${used} of ${seats} seat${seats === 1 ? '' : 's'} used${seatsFull ? ' — you’re at your limit' : ''}` : 'Invite teammates by email — they set their own password and join your company.'}
        </div>
      </Card>

      <Card className="lg:col-span-2">
        <CardHeader title="Job visibility" />
        {/* Written to ASD-STE100 Simplified Technical English at the owner's request: short
            sentences, active voice, one idea per sentence, no contractions. */}
        <div className="p-5">
          {/* Full-bleed prose is hard to read, so the copy keeps a measure even though the
              card now spans both columns. */}
          <label className="flex max-w-2xl items-start gap-2.5">
            <input
              type="checkbox"
              checked={company?.crew_can_view_all_jobs ?? false}
              disabled={!company}
              onChange={(e) => save.mutate({ crew_can_view_all_jobs: e.target.checked })}
              className="mt-0.5 h-4 w-4 accent-brand"
            />
            <span className="text-[13.5px] text-token-primary">
              Show all company jobs to all team members
              <span className="mt-1.5 block space-y-2 text-[12px] leading-relaxed text-token-secondary">
                <span className="block">If this setting is off, a team member sees only two types of job:</span>
                <span className="block pl-3">1. The jobs that you assign to that person.</span>
                <span className="block pl-3">2. The jobs that have no assigned person.</span>
                <span className="block">If this setting is on, a team member sees all the jobs of the company. The team member can also search all the jobs.</span>
                <span className="block">This setting does not give permission to change a job. Only an administrator or the assigned person can change a job.</span>
              </span>
            </span>
          </label>
        </div>
      </Card>

      <Card>
        <CardHeader title="Vehicles" count={vehicles.length} action={<button className={btnGhost} onClick={() => setVeh({ model: '', license_plate: '' })}><Plus className="h-4 w-4" /> Add</button>} />
        {vehicles.length === 0 ? (
          <EmptyState label="No vehicles. Add your fleet to assign techs and depots." cta="Add vehicle" onCta={() => setVeh({ model: '', license_plate: '' })} />
        ) : (
          <ul className="divide-y divide-token-subtle">
            {vehicles.map((v) => (
              <li key={v.id} className="group flex items-center justify-between px-5 py-3">
                <div className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-[9px] bg-canvas-subtle text-token-secondary"><Truck className="h-4 w-4" /></span>
                  <div>
                    <p className="text-[14px] font-semibold text-token-primary">{v.model || 'Vehicle'}</p>
                    <p className="text-[12px] text-token-secondary">{v.license_plate || '—'}</p>
                  </div>
                </div>
                <button className={cn(btnGhost, hoverActions)} onClick={() => removeVehicle.mutate(v.id)}><Trash2 className="h-4 w-4 text-rose-400" /></button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Modal
        open={!!veh}
        onClose={() => setVeh(null)}
        title="Add vehicle"
        footer={<>
          <button className={btnSecondary} onClick={() => setVeh(null)}>Cancel</button>
          <button className={btnPrimary} onClick={async () => { await createVehicle.mutateAsync(veh!); toast.success('Vehicle added'); setVeh(null) }}>Save</button>
        </>}
      >
        {veh && (
          <div className="space-y-4">
            <Field label="Model / label"><Input value={veh.model} onChange={(e) => setVeh({ ...veh, model: e.target.value })} placeholder="Hilux — Liam" autoFocus /></Field>
            <Field label="Registration"><Input value={veh.license_plate} onChange={(e) => setVeh({ ...veh, license_plate: e.target.value })} placeholder="ABC123" /></Field>
          </div>
        )}
      </Modal>

      <Modal
        open={!!inv}
        onClose={() => setInv(null)}
        title="Invite a team member"
        footer={<>
          <button className={btnSecondary} onClick={() => setInv(null)}>Cancel</button>
          <button className={btnPrimary} onClick={() => sendInvite()} disabled={inviteBusy || !inv?.email}>{inviteBusy ? 'Sending…' : 'Send invite'}</button>
        </>}
      >
        {inv && (
          <div className="space-y-4">
            <p className="text-[13px] text-token-secondary">They’ll get an email to set a password and join <b>{company?.name ?? 'your company'}</b>.</p>
            <Field label="Email"><Input type="email" value={inv.email} onChange={(e) => setInv({ ...inv, email: e.target.value })} placeholder="person@email.com" autoFocus /></Field>
            <Field label="Role"><Select value={inv.role} onChange={(e) => setInv({ ...inv, role: e.target.value })}><option value="user">Team member</option><option value="company-admin">Admin</option></Select></Field>
          </div>
        )}
      </Modal>
    </div>
  )
}

/* ─────────────────────────── Tax & numbering ─────────────────────────── */
function BillingTab() {
  const [qrBusy, setQrBusy] = useState(false)
  const onQr = async (file: File) => {
    if (file.size > 2_000_000) return toast.error('QR image must be under 2 MB.')
    setQrBusy(true)
    try {
      const url = await uploadPaymentQr(file)
      await save.mutateAsync({ payment_qr_url: url })
      toast.success('Payment QR saved')
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Upload failed') }
    finally { setQrBusy(false) }
  }
  const { company, isLoading, save } = useCompany()
  const [f, setF] = useState<Record<string, unknown>>({})
  const [loaded, setLoaded] = useState(false)
  if (company && !loaded) {
    setLoaded(true)
    setF({
      gst_registered: company.gst_registered ?? true,
      default_tax_rate: company.default_tax_rate ?? 10,
      quote_prefix: company.quote_prefix ?? 'Q-',
      invoice_prefix: company.invoice_prefix ?? 'INV-',
      payment_terms_days: company.payment_terms_days ?? 14,
      deposit_default_pct: company.deposit_default_pct ?? 0,
      default_terms: company.default_terms ?? '',
      auto_create_job_on_accept: company.auto_create_job_on_accept ?? true,
      bank_account_name: company.bank_account_name ?? '',
      bank_bsb: company.bank_bsb ?? '',
      bank_account_number: company.bank_account_number ?? '',
      payment_instructions: company.payment_instructions ?? '',
    })
  }
  if (isLoading) return <Card><SkeletonRows /></Card>
  const submit = async () => {
    try {
      await save.mutateAsync({
        gst_registered: f.gst_registered as boolean,
        default_tax_rate: f.default_tax_rate as number,
        quote_prefix: f.quote_prefix as string,
        invoice_prefix: f.invoice_prefix as string,
        payment_terms_days: f.payment_terms_days as number,
        deposit_default_pct: f.deposit_default_pct as number,
        default_terms: f.default_terms as string,
        auto_create_job_on_accept: f.auto_create_job_on_accept as boolean,
        bank_account_name: (f.bank_account_name as string) || null,
        bank_bsb: (f.bank_bsb as string) || null,
        bank_account_number: (f.bank_account_number as string) || null,
        payment_instructions: (f.payment_instructions as string) || null,
      })
      toast.success('Saved')
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Failed') }
  }
  return (
    <div className="space-y-5">
    {FEATURES.paywall && <SubscriptionCard />}
    <Card className="max-w-2xl">
      <CardHeader title="Tax, numbering & terms" />
      <div className="grid gap-4 p-5 sm:grid-cols-2">
        <Field label="VAT registered">
          <Select value={String(f.gst_registered)} onChange={(e) => setF({ ...f, gst_registered: e.target.value === 'true' })}>
            <option value="true">Yes, charge VAT</option>
            <option value="false">No</option>
          </Select>
        </Field>
        <Field label="Default tax rate (%)"><Input type="number" value={Number(f.default_tax_rate ?? 0)} onChange={(e) => setF({ ...f, default_tax_rate: e.target.valueAsNumber || 0 })} /></Field>
        {FEATURES.stripePayments && (
        <Field label="Default deposit on quotes (%)">
          <Select value={String(f.deposit_default_pct ?? 0)} onChange={(e) => setF({ ...f, deposit_default_pct: Number(e.target.value) })}>
            <option value="0">No deposit (default)</option>
            <option value="10">10%</option>
            <option value="20">20%</option>
            <option value="25">25%</option>
            <option value="50">50%</option>
          </Select>
        </Field>
        )}
        <Field label="Quote number prefix"><Input value={String(f.quote_prefix ?? '')} onChange={(e) => setF({ ...f, quote_prefix: e.target.value })} /></Field>
        <Field label="Invoice number prefix"><Input value={String(f.invoice_prefix ?? '')} onChange={(e) => setF({ ...f, invoice_prefix: e.target.value })} /></Field>
        <Field label="Payment terms (days)"><Input type="number" value={Number(f.payment_terms_days ?? 0)} onChange={(e) => setF({ ...f, payment_terms_days: e.target.valueAsNumber || 0 })} /></Field>
        <Field label="Default terms" className="sm:col-span-2"><Textarea rows={3} value={String(f.default_terms ?? '')} onChange={(e) => setF({ ...f, default_terms: e.target.value })} placeholder="Payment due within 7 days of the invoice." /></Field>
        <label className="flex items-start gap-2.5 sm:col-span-2">
          <input type="checkbox" checked={f.auto_create_job_on_accept !== false} onChange={(e) => setF({ ...f, auto_create_job_on_accept: e.target.checked })} className="mt-0.5 h-4 w-4 accent-brand" />
          <span className="text-[13.5px] text-token-primary">Auto-create a job when a quote is accepted<span className="block text-[12px] text-token-secondary">The job is created unscheduled &amp; unassigned for you to schedule and assign a team member. Turn off to create jobs manually.</span></span>
        </label>
      </div>
      <div className="flex justify-end border-t border-token-subtle px-5 py-3.5">
        <button className={btnPrimary} onClick={submit} disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save'}</button>
      </div>
    </Card>

    <Card className="max-w-2xl">
      <CardHeader title="Bank details (InstaPay or PesoNET)" />
      <div className="px-5 pt-4">
        <p className="text-[13px] leading-relaxed text-token-secondary">Shown on invoices you send so customers can pay by bank transfer. Leave blank to hide.</p>
      </div>
      <div className="grid gap-4 p-5 sm:grid-cols-2">
        <Field label="Account name" className="sm:col-span-2"><Input value={String(f.bank_account_name ?? '')} onChange={(e) => setF({ ...f, bank_account_name: e.target.value })} placeholder="Santos Aircon Services" /></Field>
        <Field label="Bank"><Input value={String(f.bank_bsb ?? '')} onChange={(e) => setF({ ...f, bank_bsb: e.target.value })} placeholder="BDO, BPI, Metrobank, GCash" /></Field>
        <Field label="Account number"><Input value={String(f.bank_account_number ?? '')} onChange={(e) => setF({ ...f, bank_account_number: e.target.value })} placeholder="12345678" inputMode="numeric" /></Field>
        <Field label="Payment instructions" className="sm:col-span-2"><Textarea rows={2} value={String(f.payment_instructions ?? '')} onChange={(e) => setF({ ...f, payment_instructions: e.target.value })} placeholder="Please use your invoice number as the payment reference. PayID: pay@theusefulapps.com" /></Field>
        {/* Spec 045. A payment QR (GCash, Maya, PayID) shown on the hosted invoice. Not gated by
            country: the mechanism is identical everywhere, only the wallet differs. */}
        <Field label="Payment QR (GCash, Maya or QR Ph)" className="sm:col-span-2">
          <div className="flex items-center gap-3">
            {company?.payment_qr_url && <img src={company.payment_qr_url} alt="Payment QR" className="h-16 w-16 rounded-[8px] border border-token-subtle object-contain" />}
            <label className={btnSecondary}>
              {qrBusy ? 'Uploading…' : company?.payment_qr_url ? 'Replace QR' : 'Upload QR'}
              <input type="file" accept="image/*" className="hidden" disabled={qrBusy} onChange={(e) => { const file = e.target.files?.[0]; if (file) onQr(file) }} />
            </label>
            {company?.payment_qr_url && (
              <button className="text-[12px] font-semibold text-token-muted hover:text-token-danger" disabled={qrBusy}
                onClick={async () => { await save.mutateAsync({ payment_qr_url: null }); toast.success('Payment QR removed') }}>Remove</button>
            )}
          </div>
          <span className="mt-1 block text-[12px] leading-relaxed text-token-secondary">
            Export the QR from your payment app (GCash: Show My QR &rarr; save as image; Maya the
            same; PayID: your banking app) and upload it here. It shows on your invoices so
            customers scan and pay. The payment is not confirmed automatically; mark the invoice
            paid when the money arrives.
          </span>
        </Field>
      </div>
      <div className="flex justify-end border-t border-token-subtle px-5 py-3.5">
        <button className={btnPrimary} onClick={submit} disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save'}</button>
      </div>
    </Card>
    </div>
  )
}

/* ─────────────────────────── SMS ─────────────────────────── */
function SmsTab() {
  const { company, save } = useCompany()
  const qc = useQueryClient()
  const FREE = 20
  const credits = company?.sms_credits ?? 0
  const [busy, setBusy] = useState<string | null>(null)
  const [to, setTo] = useState('')
  const remindersOn = company?.sms_reminders_enabled ?? true

  const usage = useQuery({
    queryKey: ['sms-usage'],
    queryFn: async () => {
      const m = new Date(); m.setDate(1); m.setHours(0, 0, 0, 0)
      const { data } = await supabase.from('sms_message').select('kind').gte('created_at', m.toISOString())
      return (data ?? []) as { kind: string }[]
    },
  })
  const freeUsed = (usage.data ?? []).filter((x) => x.kind === 'free').length
  const freeLeft = Math.max(0, FREE - freeUsed)

  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get('sms')
    if (!p) return
    if (p === 'success') { toast.success('SMS credits added'); qc.invalidateQueries({ queryKey: ['company'] }); qc.invalidateQueries({ queryKey: ['sms-usage'] }) }
    else toast.message('Checkout cancelled')
    window.history.replaceState({}, '', window.location.pathname)
  }, [qc])

  const [pack, setPack] = useState<'small' | 'medium' | 'large'>('large')
  // ServicePilot's AUD amounts, hidden while FEATURES.stripePayments is off. Set peso prices here and
  // in sms-checkout before turning it on.
  const PACKS = { small: { sms: '100', price: 18 }, medium: { sms: '300', price: 45 }, large: { sms: '1,000', price: 130 } } as const

  const buy = async (pack: 'small' | 'medium' | 'large') => {
    setBusy(pack)
    const { data, error } = await supabase.functions.invoke('sms-checkout', { body: { pack } })
    setBusy(null)
    if (error || !(data as { url?: string })?.url) return toast.error('Could not start checkout')
    window.location.href = (data as { url: string }).url
  }
  const test = async () => {
    if (!to.trim()) return toast.error('Enter a mobile number (e.g. 0917 123 4567)')
    setBusy('test')
    const { data, error } = await supabase.functions.invoke('send-sms', { body: { to, body: 'Test SMS from MySerbisyo ✅' } })
    setBusy(null)
    if (error || !(data as { ok?: boolean })?.ok) {
      const ctx = (error as { context?: Response } | null)?.context
      let msg = 'Failed to send'
      try { if (ctx) { const j = await ctx.json(); if (j?.error) msg = j.error } } catch { /* */ }
      return toast.error(msg)
    }
    toast.success('Test SMS sent'); qc.invalidateQueries({ queryKey: ['sms-usage'] })
  }

  const packBtn = 'flex flex-col items-start rounded-[14px] border p-4 text-left transition hover:shadow-sm'
  return (
    <div className="space-y-5">
      <Card className="max-w-2xl">
        <CardHeader title="SMS credits" />
        <div className="p-5">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="rounded-[12px] bg-token-surface p-4"><p className="text-[12px] font-semibold uppercase tracking-wide text-token-secondary">Free this month</p><p className="font-display mt-1 text-[24px] font-bold text-token-primary">{freeLeft}<span className="text-[14px] font-normal text-token-secondary"> / {FREE}</span></p></div>
            <div className="rounded-[12px] bg-token-surface p-4"><p className="text-[12px] font-semibold uppercase tracking-wide text-token-secondary">Purchased credits</p><p className="font-display mt-1 text-[24px] font-bold text-token-primary">{credits}</p></div>
          </div>
          <p className="mt-3 text-[12.5px] text-token-secondary">Every account gets {FREE} free SMS a month. After that, sending uses purchased credits — and stops at zero, so you’re never billed for texts you didn’t buy.</p>
          <SmsLog />
          {/* Bought by card through Stripe, which MySerbisyo does not use (spec 001). */}
          {FEATURES.stripePayments && (<>
          <p className="mt-4 mb-2 text-[13px] font-bold text-token-primary">Top up</p>
          <div className="grid gap-3 sm:grid-cols-3" role="radiogroup" aria-label="Choose an SMS pack">
            {(['small', 'medium', 'large'] as const).map((k) => (
              <button
                key={k}
                role="radio"
                aria-checked={pack === k}
                onClick={() => setPack(k)}
                className={cn(packBtn, 'relative', pack === k ? 'border-token-strong bg-token-warning-surface ring-2 ring-accent' : 'border-token-subtle hover:border-token-subtle')}
              >
                {pack === k && <span className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-brand text-[11px] font-bold text-accent">✓</span>}
                <span className={cn('text-[12px] font-bold uppercase', k === 'large' ? 'text-token-warning' : 'text-token-secondary')}>{PACKS[k].sms} SMS{k === 'large' ? ' · best value' : ''}</span>
                <span className="font-display text-[22px] font-bold text-token-primary">{peso(PACKS[k].price, { whole: true })}</span>
                <span className="text-[12px] text-token-secondary">{(PACKS[k].price * 100 / Number(PACKS[k].sms.replace(',', ''))).toFixed(0)}¢ each</span>
              </button>
            ))}
          </div>
          <button className={cn(btnPrimary, 'mt-4 w-full justify-center sm:w-auto')} onClick={() => buy(pack)} disabled={busy === pack}>
            {busy === pack ? 'Opening secure checkout…' : `Buy ${PACKS[pack].sms} SMS for ${peso(PACKS[pack].price, { whole: true })}`}
          </button>
          <p className="mt-2 text-[12px] text-token-secondary">Secure card payment via Stripe. Credits are added to your balance automatically as soon as payment completes.</p>
          </>)}
        </div>
      </Card>

      <Card className="max-w-2xl">
        <CardHeader title="Automatic follow-ups" />
        <div className="space-y-4 p-5">
          <label className="flex items-start gap-2.5">
            <input type="checkbox" checked={company?.quote_followups_enabled ?? false} onChange={(e) => save.mutate({ quote_followups_enabled: e.target.checked })} className="mt-0.5 h-4 w-4 accent-brand" />
            <span className="text-[13.5px] text-token-primary">Follow up quotes automatically<span className="block text-[12px] text-token-secondary">A polite email nudge 3 days after a quote is sent and again at 7 days if there's no decision (max 2, stops the moment they accept or decline). Free — sent by email, doesn't use SMS credits.</span></span>
          </label>
          <label className="flex items-start gap-2.5">
            <input type="checkbox" checked={company?.invoice_reminders_enabled ?? false} onChange={(e) => save.mutate({ invoice_reminders_enabled: e.target.checked })} className="mt-0.5 h-4 w-4 accent-brand" />
            <span className="text-[13.5px] text-token-primary">Chase overdue invoices automatically<span className="block text-[12px] text-token-secondary">A friendly reminder 3 days after the due date and a firmer one at 10 days (max 2), with the pay-online link. Stops as soon as the invoice is paid.</span></span>
          </label>
        </div>
      </Card>

      <Card className="max-w-2xl">
        <CardHeader title="Automatic reminders" />
        <div className="p-5">
          <label className="flex items-start gap-2.5">
            <input type="checkbox" checked={remindersOn} onChange={(e) => save.mutate({ sms_reminders_enabled: e.target.checked })} className="mt-0.5 h-4 w-4 accent-brand" />
            <span className="text-[13.5px] text-token-primary">Text customers a reminder the day before their job<span className="block text-[12px] text-token-secondary">Sent the evening before a scheduled job (once per job) to customers with a mobile number who haven’t opted out. Uses your SMS allowance/credits.</span></span>
          </label>
        </div>
      </Card>

      <Card className="max-w-2xl">
        <CardHeader title="Send a test" />
        <div className="space-y-3 p-5">
          <Field label="Mobile number"><Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="0917 123 4567" /></Field>
          <button className={btnPrimary} onClick={test} disabled={busy === 'test'}>{busy === 'test' ? 'Sending…' : 'Send test SMS'}</button>
          <p className="text-[12px] text-token-secondary">Uses 1 SMS from your free allowance or credits. Keep messages under 160 characters to avoid double charges.</p>
        </div>
      </Card>
    </div>
  )
}

/* ─────────────────────────── Integrations hub ─────────────────────────── */
const INTEGRATIONS = [
  { id: 'xero', name: 'Xero', desc: 'Sync invoices, payments & contacts to your accounting.', cat: 'Accounting' },
  { id: 'quickbooks', name: 'QuickBooks', desc: 'Push invoices & payments to QuickBooks Online.', cat: 'Accounting' },
  { id: 'reckon', name: 'Reckon One', desc: 'Push invoices & payments to Reckon One accounting.', cat: 'Accounting' },
  { id: 'stripe', name: 'Stripe', desc: 'Let customers pay invoices by card. Funds settle directly to your own Stripe account.', cat: 'Payments' },
]

function IntegrationsTab() {
  const qc = useQueryClient()
  const [busy, setBusy] = useState<string | null>(null)

  const xero = useQuery({
    queryKey: ['xero-connection'],
    queryFn: async () => (await supabase.from('xero_connection').select('tenant_name').maybeSingle()).data as { tenant_name: string | null } | null,
  })
  const stripe = useQuery({
    queryKey: ['stripe-connection'],
    queryFn: async () => (await supabase.from('stripe_connection').select('stripe_account_id, livemode').maybeSingle()).data as { stripe_account_id: string; livemode: boolean } | null,
  })

  // Surface the OAuth result after Xero/Stripe redirect back (?xero=… / ?stripe=…).
  useEffect(() => {
    const p = new URLSearchParams(window.location.search)
    const x = p.get('xero'), s = p.get('stripe')
    if (x) {
      if (x === 'connected') toast.success(`Xero connected${p.get('org') ? ` · ${p.get('org')}` : ''}`)
      else toast.error(`Xero connection failed${p.get('detail') ? `: ${p.get('detail')}` : ''}`)
      qc.invalidateQueries({ queryKey: ['xero-connection'] })
    }
    if (s) {
      if (s === 'connected') toast.success('Stripe connected — you can now take card payments')
      else toast.error(`Stripe connection failed${p.get('detail') ? `: ${p.get('detail')}` : ''}`)
      qc.invalidateQueries({ queryKey: ['stripe-connection'] })
    }
    if (x || s) window.history.replaceState({}, '', window.location.pathname)
  }, [qc])

  const startConnect = async (fn: string, id: string, label: string) => {
    setBusy(id)
    const { data, error } = await supabase.functions.invoke(fn, { body: { returnUrl: `${window.location.origin}/settings` } })
    setBusy(null)
    if (error || !(data as { url?: string })?.url) return toast.error(`Could not start ${label} connection`)
    window.location.href = (data as { url: string }).url
  }
  const disconnect = async (table: string, key: string, label: string, warn: string) => {
    if (!confirm(warn)) return
    const { error } = await supabase.from(table).delete().not('company_id', 'is', null)
    if (error) return toast.error(error.message)
    toast.success(`${label} disconnected`)
    qc.invalidateQueries({ queryKey: [key] })
  }

  return (
    <>
    <SectionHead title="Integrations" sub="Connect MySerbisyo to the tools you already run on." />
    <div className="grid gap-4 sm:grid-cols-2">
      {INTEGRATIONS.map((i) => {
        const isXero = i.id === 'xero', isStripe = i.id === 'stripe'
        const on = (isXero && !!xero.data) || (isStripe && !!stripe.data)
        const sub = isXero && on ? (xero.data?.tenant_name || 'Connected')
          : isStripe && on ? `Connected${stripe.data?.livemode === false ? ' · test mode' : ''}`
          : i.cat
        return (
          <Card key={i.id}>
            <div className="flex items-start justify-between p-5">
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-[10px] bg-canvas-subtle text-[13px] font-bold text-token-secondary">{i.name[0]}</span>
                <div>
                  <p className="text-[14.5px] font-bold text-token-primary">{i.name}</p>
                  <p className="mt-0.5 text-[12.5px] text-token-secondary">{sub}</p>
                </div>
              </div>
              {on
                ? <span className="inline-flex items-center gap-1 rounded-full bg-token-success-surface px-2.5 py-0.5 text-[11.5px] font-bold text-token-success"><Check className="h-3 w-3" /> Connected</span>
                : <span className="rounded-full bg-canvas-subtle px-2.5 py-0.5 text-[11.5px] font-bold text-token-secondary">Not connected</span>}
            </div>
            <p className="px-5 pb-4 text-[13px] text-token-secondary">{i.desc}</p>
            {isStripe && on && <CardSurcharge />}
            <div className="border-t border-token-subtle px-5 py-3">
              {isXero ? (
                on
                  ? <button className={btnSecondary} onClick={() => disconnect('xero_connection', 'xero-connection', 'Xero', 'Disconnect Xero? Invoices will no longer sync.')}>Disconnect</button>
                  : <button className={btnPrimary} onClick={() => startConnect('xero-connect', 'xero', 'Xero')} disabled={busy === 'xero'}>{busy === 'xero' ? 'Redirecting…' : 'Connect Xero'}</button>
              ) : isStripe ? (
                on
                  ? <button className={btnSecondary} onClick={() => disconnect('stripe_connection', 'stripe-connection', 'Stripe', 'Disconnect Stripe? Customers will no longer be able to pay invoices by card.')}>Disconnect</button>
                  : <button className={btnPrimary} onClick={() => startConnect('stripe-connect', 'stripe', 'Stripe')} disabled={busy === 'stripe'}>{busy === 'stripe' ? 'Redirecting…' : 'Connect Stripe'}</button>
              ) : (
                <button className={btnSecondary} onClick={() => toast.message(`${i.name} — coming soon`, { description: `We'll let you know when the ${i.name} integration is ready.` })}>Coming soon</button>
              )}
            </div>
          </Card>
        )
      })}
    </div>
    </>
  )
}


/**
 * 011 T007 — what the credits were actually spent on.
 *
 * The card showed a balance and nothing else, so "where did my SMS go" had no answer anywhere in
 * the product. Every send is already recorded in sms_message with its segment count; a text over
 * 160 characters costs two credits, and that is the single most common reason a balance drops
 * faster than expected. So segments are shown per row rather than a bare count of messages.
 */
function SmsLog() {
  const [open, setOpen] = useState(false)
  const { data = [], isLoading } = useQuery({
    queryKey: ['sms-log'],
    enabled: open,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sms_message')
        .select('id, to_number, kind, status, segments, created_at, task_id')
        .order('created_at', { ascending: false })
        .limit(50)
      if (error) throw error
      return data as Array<{
        id: number; to_number: string | null; kind: string | null; status: string | null
        segments: number | null; created_at: string; task_id: number | null
      }>
    },
  })

  return (
    <div className="mt-4 border-t border-token-subtle pt-4">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="text-[13px] font-bold text-token-primary underline-offset-2 hover:underline"
      >
        {open ? 'Hide' : 'Show'} recent messages
      </button>
      {open && (
        <div className="mt-3 overflow-hidden rounded-[10px] border border-token-subtle">
          {isLoading ? (
            <p className="px-4 py-6 text-center text-[13px] text-token-secondary">Loading…</p>
          ) : data.length === 0 ? (
            <p className="px-4 py-6 text-center text-[13px] text-token-secondary">No messages sent yet.</p>
          ) : (
            <table className="w-full text-[12.5px]">
              <thead className="bg-token-surface text-token-secondary">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">Sent</th>
                  <th className="px-3 py-2 text-left font-semibold">To</th>
                  <th className="px-3 py-2 text-left font-semibold">Kind</th>
                  <th className="px-3 py-2 text-right font-semibold">Credits</th>
                  <th className="px-3 py-2 text-right font-semibold">Result</th>
                </tr>
              </thead>
              <tbody>
                {data.map((m) => (
                  <tr key={m.id} className="border-t border-token-subtle">
                    <td className="px-3 py-2 tabular-nums text-token-secondary">
                      {phFormat(new Date(m.created_at), 'datetime', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}
                    </td>
                    <td className="px-3 py-2 tabular-nums text-token-primary">{m.to_number ?? '—'}</td>
                    <td className="px-3 py-2 text-token-secondary">{(m.kind ?? '').replace(/[-_]/g, ' ') || '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-token-primary">{m.segments ?? 1}</td>
                    <td className={cn('px-3 py-2 text-right font-semibold',
                      m.status === 'failed' ? 'text-token-danger' : 'text-token-success')}>
                      {m.status ?? 'sent'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * 014 T009 — pass on card fees.
 *
 * It sits inside the Stripe card and only when Stripe is connected, because a surcharge with no
 * card payments to apply it to is a setting that does nothing.
 *
 * The guidance is not decoration. Australian rules say a surcharge may not exceed the merchant's
 * actual cost of acceptance, which is a number only they know, so the field asks for theirs rather
 * than offering a tempting default. The database caps it at 2.5 as well, because a form validates
 * a person and a constraint validates every other path in.
 */
function CardSurcharge() {
  const { company, save } = useCompany()
  const [value, setValue] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const current = company?.card_surcharge_pct
  const shown = value ?? (current == null ? '' : String(current))
  const parsed = shown.trim() === '' ? null : Number(shown)
  const invalid = parsed !== null && (!Number.isFinite(parsed) || parsed < 0 || parsed > 2.5)
  const dirty = shown !== (current == null ? '' : String(current))

  const commit = async () => {
    if (invalid) return
    setSaving(true)
    try {
      await save.mutateAsync({ card_surcharge_pct: parsed === null || parsed === 0 ? null : parsed })
      toast.success(parsed ? `Card payments will add ${parsed}%` : 'Card surcharge turned off')
      setValue(null)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not save')
    } finally { setSaving(false) }
  }

  return (
    <div className="border-t border-token-subtle px-5 py-4">
      <p className="text-[13px] font-bold text-token-primary">Pass on card fees</p>
      <div className="mt-2.5 flex items-start gap-2">
        <div className="relative">
          <Input
            value={shown}
            onChange={(e) => setValue(e.target.value)}
            onBlur={commit}
            inputMode="decimal"
            placeholder="0"
            aria-label="Card surcharge percent"
            className={cn('w-[110px] pr-7', invalid && 'border-token-danger')}
          />
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[13px] text-token-secondary">%</span>
        </div>
        {dirty && !invalid && (
          <button className={btnSecondary} onClick={commit} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        )}
      </div>
      {invalid ? (
        <p className="mt-2 text-[12px] text-token-danger">Enter a number between 0 and 2.5.</p>
      ) : (
        <p className="mt-2 text-[12px] leading-relaxed text-token-secondary">
          Added to <b>card payments only</b>. Bank transfer is never surcharged, and the amount is
          shown to your customer before they pay. Australian rules allow you to recover what card
          acceptance actually costs you and no more, so use your own merchant rate — commonly 1.0%
          to 1.9%. Leave blank to charge nothing.
        </p>
      )}
    </div>
  )
}

/* ─────────────────────────── Notification schedules ─────────────────────────── */
const REL_DATES = [
  { v: 'planned_start_date', l: 'Planned start date' },
  { v: 'planned_end_date', l: 'Planned end date' },
  { v: 'actual_start_date', l: 'Actual start date' },
  { v: 'actual_end_date', l: 'Actual end date' },
]
const REL_TYPES = ['minutes', 'hours', 'days', 'months', 'years']

function offsetLabel(s: NotificationSchedule): string {
  if (!s.relative_to_date) return 'When the job is created'
  const v = s.relative_to_date_value ?? 0
  const rel = REL_DATES.find((r) => r.v === s.relative_to_date)?.l ?? s.relative_to_date
  if (v === 0) return `On ${rel}`
  return `${Math.abs(v)} ${s.relative_to_date_type} ${v < 0 ? 'before' : 'after'} ${rel}`
}

function SchedulesTab() {
  const { schedules, forms, templates, saveSchedule, removeSchedule } = useNotifications()
  // 048 T049 (FR-013). Opt-in, one rule at a time, and it does NOT disable the original — the
  // admin turns the old rule off once satisfied. Converting and disabling in one step means a
  // mistake in the mapping silently stops a customer's booking confirmations.
  const [converting, setConverting] = useState<NotificationSchedule | null>(null)
  const publishWorkflow = usePublishWorkflow()
  const navigate = useNavigate()
  const { data: sends } = useScheduleSends()
  const { data: trouble } = useScheduleTrouble()
  const [editing, setEditing] = useState<Partial<NotificationSchedule> | null>(null)

  const submit = async () => {
    if (!editing?.name?.trim()) return toast.error('Give the schedule a name.')
    if (!editing.custom_form_id) return toast.error('Pick a job form.')
    if (!editing.notification_email_template_id) return toast.error('Pick an email template.')
    try { await saveSchedule.mutateAsync(editing); toast.success('Schedule saved'); setEditing(null) }
    catch (e) { toast.error(e instanceof Error ? e.message : 'Failed') }
  }
  const templateName = (id: number | null) => templates.find((t) => t.id === id)?.name ?? templates.find((t) => t.id === id)?.subject ?? '—'
  const formName = (id: string | null) => forms.find((f) => f.id === id)?.name ?? '—'

  return (
    <>
      <SectionHead title="Notification schedules" sub="Automatically email customers relative to a job's dates — reminders, follow-ups, and more." />
      <Card>
        <CardHeader title="Schedules" count={schedules.length} action={<button className={btnPrimary} onClick={() => setEditing({ name: 'Booking confirmation', relative_to_date: null, relative_to_date_type: null, relative_to_date_value: null, condition: conditionFor('All') })}><Plus className="h-4 w-4" /> New schedule</button>} />
        {schedules.length === 0 ? (
          <EmptyState label="No notification schedules yet." cta="New schedule" onCta={() => setEditing({ relative_to_date: 'planned_start_date', relative_to_date_type: 'days', relative_to_date_value: -1, condition: conditionFor('All') })} />
        ) : (
          <ul className="divide-y divide-token-subtle">
            {schedules.map((s) => (
              <li key={s.id} className={cn('group flex items-center justify-between px-5 py-3 hover:bg-token-surface', s.enabled === false && 'opacity-55')}>
                <div className="flex min-w-0 items-center gap-3">
                  <span className="flex h-9 w-9 flex-none items-center justify-center rounded-[9px] bg-token-info-surface text-token-info"><BellRing className="h-4 w-4" /></span>
                  <div className="min-w-0">
                    <p className="truncate text-[14px] font-semibold text-token-primary">{s.name || 'Untitled'}</p>
                    <p className="truncate text-[12px] text-token-secondary">{offsetLabel(s)} · {formName(s.custom_form_id)} · {statusFromCondition(s.condition)} · ✉ {templateName(s.notification_email_template_id)}</p>
                    <p className="truncate text-[11.5px] text-token-muted">
                      {sends?.[s.id]
                        ? `Last sent ${phFormat(new Date(sends[s.id].at), 'date', { day: 'numeric', month: 'short' })} · ${sends[s.id].count} sent`
                        : 'Never sent'}
                    </p>
                    {/* A systemic fault has to surface where she already looks. Per-job visibility
                        answers "did this customer get their email?"; this answers "is this rule
                        broken for everyone?", which is the question nobody could ask before. */}
                    {(trouble?.[s.id] ?? 0) > 0 && (
                      <p className="truncate text-[11.5px] font-semibold text-token-danger">
                        {trouble![s.id]} couldn’t be sent — open those jobs to see why
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {/* Always visible, not behind hover: whether a rule is live is the thing you
                      come to this screen to check, and touch devices have no hover. */}
                  <label className="flex flex-none cursor-pointer items-center gap-2" title={s.enabled === false ? 'Paused' : 'Sending'}>
                    <input
                      type="checkbox"
                      checked={s.enabled !== false}
                      onChange={(e) => saveSchedule.mutate({ ...s, enabled: e.target.checked })}
                      className="h-4 w-4 accent-brand"
                    />
                    <span className="text-[12px] text-token-secondary">{s.enabled === false ? 'Paused' : 'On'}</span>
                  </label>
                  <div className={cn('flex items-center gap-1', hoverActions)}>
                    <button className={btnGhost} title="Convert to a workflow" aria-label={`Convert ${s.name ?? 'schedule'} to a workflow`} onClick={() => setConverting(s)}>
                      <Workflow className="h-4 w-4" />
                    </button>
                    <button className={btnGhost} onClick={() => setEditing(s)}><Pencil className="h-4 w-4" /></button>
                    <button className={btnGhost} onClick={() => removeSchedule.mutate(s.id)}><Trash2 className="h-4 w-4 text-token-danger" /></button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* 048 T049. The losses are shown BEFORE the conversion, not reported after it. An admin who
          finds out afterwards that the timing moved has already been let down. */}
      <Modal
        open={!!converting}
        onClose={() => setConverting(null)}
        title="Convert to a workflow"
        footer={
          <>
            <button className={btnSecondary} onClick={() => setConverting(null)}>Cancel</button>
            <button
              className={btnPrimary}
              disabled={publishWorkflow.isPending}
              onClick={async () => {
                if (!converting) return
                const c = scheduleToWorkflow(converting)
                const r = await publishWorkflow.mutateAsync({
                  name: c.name,
                  trigger_event_type: c.triggerEventType,
                  graph: c.graph,
                  enabled: false,      // paused. The admin turns it on after they have looked.
                })
                if (r.errors?.length || r.error) {
                  toast.error(r.error ?? r.errors![0].message)
                  return
                }
                setConverting(null)
                toast.success('Converted, paused. The original schedule is untouched.')
                if (r.workflow_id) navigate({ to: '/workflows/$id', params: { id: r.workflow_id } })
              }}
            >
              {publishWorkflow.isPending ? 'Converting…' : 'Create the workflow'}
            </button>
          </>
        }
      >
        {converting && (() => {
          const c = scheduleToWorkflow(converting)
          return (
            <div className="space-y-3">
              <p className="text-[13.5px] leading-relaxed text-token-secondary">
                This makes a new workflow from “{converting.name || 'Untitled'}”. It arrives <strong>paused</strong>,
                and <strong>this schedule keeps running exactly as it does now</strong> — turn it off yourself once
                you are happy with the workflow.
              </p>
              <div className="rounded-[10px] border border-token-subtle bg-token-surface p-3">
                <p className="text-[11px] font-semibold uppercase tracking-[0.05em] text-token-muted">What it will do</p>
                <p className="mt-1 text-[13px] text-token-primary">
                  {c.graph.nodes.map((n) => n.type).join(' → ')}
                </p>
              </div>
              {c.losses.length > 0 ? (
                <div className="rounded-[10px] border border-token bg-token-warning-surface p-3">
                  <p className="text-[12.5px] font-semibold text-token-warning">Read this before you convert</p>
                  <ul className="mt-1.5 space-y-1.5">
                    {c.losses.map((l, i) => (
                      <li key={i} className="text-[12.5px] leading-snug text-token-warning">{l}</li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="text-[12.5px] text-token-secondary">Everything in this rule converts cleanly.</p>
              )}
            </div>
          )
        })()}
      </Modal>

      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing?.id ? 'Edit schedule' : 'New schedule'} wide
        footer={<><button className={btnSecondary} onClick={() => setEditing(null)}>Cancel</button><button className={btnPrimary} onClick={submit} disabled={saveSchedule.isPending}>{saveSchedule.isPending ? 'Saving…' : 'Save schedule'}</button></>}>
        {editing && (
          <div className="space-y-4">
            <Field label="Schedule name"><Input value={editing.name ?? ''} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="1-day job reminder" autoFocus /></Field>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Job form"><Select value={editing.custom_form_id ?? ''} onChange={(e) => setEditing({ ...editing, custom_form_id: e.target.value })}><option value="">Select…</option>{forms.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}</Select></Field>
              <Field label="Email template"><Select value={String(editing.notification_email_template_id ?? '')} onChange={(e) => setEditing({ ...editing, notification_email_template_id: e.target.value ? Number(e.target.value) : null })}><option value="">Select…</option>{templates.map((t) => <option key={t.id} value={t.id}>{t.name || t.subject}</option>)}</Select></Field>
            </div>
            <div className="rounded-[12px] border border-token-subtle bg-token-surface p-4">
              <p className="mb-2.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-token-muted">Send timing</p>
              {/* An explicit trigger. Without it there was no way to express "on creation", so
                  opening a booking confirmation and saving silently rewrote it to 0 days
                  relative to the planned start date, and it then only sent on the day of the
                  job instead of when the job was booked. */}
              <Field label="Trigger">
                <Select
                  value={editing.relative_to_date ? 'relative' : 'creation'}
                  onChange={(e) => setEditing(e.target.value === 'creation'
                    ? { ...editing, relative_to_date: null, relative_to_date_type: null, relative_to_date_value: null,
                        name: editing.name?.trim() ? editing.name : 'Booking confirmation' }
                    : { ...editing, relative_to_date: 'planned_start_date', relative_to_date_type: 'days', relative_to_date_value: -1,
                        name: editing.name?.trim() && editing.name !== 'Booking confirmation' ? editing.name : 'Job reminder' })}
                >
                  <option value="creation">When the job is created</option>
                  <option value="relative">Before or after a date on the job</option>
                </Select>
              </Field>
              {editing.relative_to_date && (
              <div className="mt-3 grid grid-cols-2 items-end gap-3 sm:grid-cols-[100px_120px_1fr]">
                <Field label="Offset"><Input type="number" value={editing.relative_to_date_value ?? 0} onChange={(e) => setEditing({ ...editing, relative_to_date_value: e.target.valueAsNumber || 0 })} /></Field>
                <Field label="Unit"><Select value={editing.relative_to_date_type ?? 'days'} onChange={(e) => setEditing({ ...editing, relative_to_date_type: e.target.value })}>{REL_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}</Select></Field>
                <Field label="Relative to"><Select value={editing.relative_to_date ?? 'planned_start_date'} onChange={(e) => setEditing({ ...editing, relative_to_date: e.target.value })}>{REL_DATES.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}</Select></Field>
              </div>
              )}
              <p className="mt-2.5 text-[12.5px] text-token-secondary">{offsetLabel(editing as NotificationSchedule)}{editing.relative_to_date ? <span className="text-token-secondary"> (negative = before, positive = after)</span> : null}</p>
            </div>
            <Field label="Only when job status is"><Select value={statusFromCondition(editing.condition ?? null)} onChange={(e) => setEditing({ ...editing, condition: conditionFor(e.target.value) })}>{SCHEDULE_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}</Select></Field>
          </div>
        )}
      </Modal>
    </>
  )
}

// Spec 018 · Settings -> Online booking. Without this an operator cannot turn booking on at all,
// so the whole feature would be reachable only by editing the database directly.
const DAYS: { k: string; l: string }[] = [
  { k: 'mon', l: 'Monday' }, { k: 'tue', l: 'Tuesday' }, { k: 'wed', l: 'Wednesday' },
  { k: 'thu', l: 'Thursday' }, { k: 'fri', l: 'Friday' }, { k: 'sat', l: 'Saturday' },
  { k: 'sun', l: 'Sunday' },
]

function BookingTab() {
  const { company, save } = useCompany()
  const qc = useQueryClient()
  const [busy, setBusy] = useState(false)

  const [enabled, setEnabled] = useState(false)
  const [slug, setSlug] = useState('')
  // MySerbisyo (spec 001): one zone. Bookings are always Manila time.
  const tz = TIME_ZONE
  const [hours, setHours] = useState<Record<string, [string, string][]>>({})
  const [lead, setLead] = useState(240)
  const [horizon, setHorizon] = useState(21)
  const [maxPerDay, setMaxPerDay] = useState(4)
  const [buffer, setBuffer] = useState(30)

  useEffect(() => {
    if (!company) return
    setEnabled(!!company.booking_enabled)
    setSlug(company.booking_slug ?? '')
    setHours((company.booking_hours ?? {}) as Record<string, [string, string][]>)
    setLead(company.booking_lead_time_min ?? 240)
    setHorizon(company.booking_horizon_days ?? 21)
    setMaxPerDay(company.booking_max_per_day ?? 4)
    setBuffer(company.booking_buffer_min ?? 30)
  }, [company])

  const services = useQuery({
    queryKey: ['booking-services'],
    queryFn: async () => {
      const { data, error } = await supabase.from('booking_service')
        .select('id, name, duration_min, price, active, sort').order('sort')
      if (error) throw error
      return (data ?? []) as { id: string; name: string; duration_min: number; price: number | null; active: boolean; sort: number }[]
    },
  })

  const [svcName, setSvcName] = useState('')
  const [svcMins, setSvcMins] = useState(60)
  const [svcPrice, setSvcPrice] = useState('')

  const cleanSlug = (v: string) => v.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '')
  const bookingUrl = slug ? `${window.location.origin}/book/${slug}` : ''

  const setDay = (k: string, on: boolean) =>
    setHours((h) => { const n = { ...h }; if (on) n[k] = n[k]?.length ? n[k] : [['08:00', '16:00']]; else delete n[k]; return n })
  const setRange = (k: string, i: 0 | 1, v: string) =>
    setHours((h) => { const n = { ...h }; const r = [...(n[k]?.[0] ?? ['08:00', '16:00'])] as [string, string]; r[i] = v; n[k] = [r]; return n })

  const persist = async () => {
    const s = cleanSlug(slug)
    if (enabled && !s) { toast.error('Pick a booking link before turning it on.'); return }
    setBusy(true)
    try {
      await save.mutateAsync({
        booking_enabled: enabled, booking_slug: s || null, booking_timezone: tz,
        booking_hours: hours, booking_lead_time_min: lead, booking_horizon_days: horizon,
        booking_max_per_day: maxPerDay, booking_buffer_min: buffer,
      } as never)
      setSlug(s)
      toast.success('Booking settings saved')
    } catch (e) {
      // The slug has a unique index; a clash is the one failure a user can actually act on.
      const msg = e instanceof Error && /duplicate|unique/i.test(e.message)
        ? 'That booking link is already taken — try another.'
        : e instanceof Error ? e.message : 'Could not save'
      toast.error(msg)
    } finally { setBusy(false) }
  }

  const addService = async () => {
    if (!svcName.trim()) { toast.error('Give the service a name.'); return }
    const { error } = await supabase.from('booking_service').insert({
      company_id: company?.id, name: svcName.trim(), duration_min: svcMins,
      price: svcPrice ? Number(svcPrice) : null, sort: (services.data?.length ?? 0) + 1,
    })
    if (error) return toast.error(error.message)
    setSvcName(''); setSvcMins(60); setSvcPrice('')
    qc.invalidateQueries({ queryKey: ['booking-services'] })
  }

  const removeService = async (id: string) => {
    const { error } = await supabase.from('booking_service').update({ active: false }).eq('id', id)
    if (error) return toast.error(error.message)
    qc.invalidateQueries({ queryKey: ['booking-services'] })
  }

  return (
    <div className="flex flex-col gap-5">
      <Card>
        <CardHeader title="Online booking" />
        <div className="px-5 pb-5">
          <label className="flex items-center gap-2.5">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="h-4 w-4" />
            <span className="text-[14px] font-medium text-token-primary">Let customers book online</span>
          </label>
          <p className="mt-1.5 text-[12.5px] text-token-secondary">
            Customers pick a free time from your calendar. Each booking creates an unassigned job and a lead.
          </p>

          {/* Booking on with no services is a live link that tells customers "this business hasn't
              listed any bookable services yet". The trade person thinks they are taking bookings and
              nobody can book. The old hint sat below the fold in grey; this is where the decision
              is made. */}
          {enabled && (services.data?.filter((s) => s.active).length ?? 0) === 0 && (
            <div className="mt-3 flex items-start gap-2.5 rounded-[10px] border border-token-warning bg-token-warning-surface px-3.5 py-3 text-[13px] text-token-warning">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-none" />
              <span>
                <strong>Nobody can book yet.</strong> Your link is live, but customers see an empty page until you
                add at least one service below.
              </span>
            </div>
          )}

          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Field label="Booking link">
              <Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="acme-plumbing" />
              {bookingUrl && (
                <p className="mt-1 break-all text-[12px] text-token-secondary">
                  {bookingUrl}
                  <button className="ml-2 font-semibold text-token-primary" onClick={() => { navigator.clipboard.writeText(bookingUrl); toast.success('Link copied') }}>Copy</button>
                </p>
              )}
            </Field>
          </div>

          <p className="mt-5 text-[11px] font-black uppercase tracking-[0.12em] text-token-secondary">Hours you accept bookings</p>
          <div className="mt-2 space-y-2">
            {DAYS.map((d) => {
              const on = !!hours[d.k]?.length
              const r = hours[d.k]?.[0] ?? ['08:00', '16:00']
              return (
                <div key={d.k} className="flex flex-wrap items-center gap-3">
                  <label className="flex w-[130px] items-center gap-2">
                    <input type="checkbox" checked={on} onChange={(e) => setDay(d.k, e.target.checked)} className="h-4 w-4" />
                    <span className="text-[13.5px] text-token-primary">{d.l}</span>
                  </label>
                  {on && (
                    <>
                      <Input type="time" value={r[0]} onChange={(e) => setRange(d.k, 0, e.target.value)} className="w-[120px]" />
                      <span className="text-[13px] text-token-secondary">to</span>
                      <Input type="time" value={r[1]} onChange={(e) => setRange(d.k, 1, e.target.value)} className="w-[120px]" />
                    </>
                  )}
                  {!on && <span className="text-[13px] text-token-muted">Closed</span>}
                </div>
              )
            })}
          </div>

          <div className="mt-5 grid gap-4 sm:grid-cols-4">
            <Field label="Notice needed (min)"><Input type="number" min={0} value={lead} onChange={(e) => setLead(e.target.valueAsNumber || 0)} /></Field>
            <Field label="Book up to (days)"><Input type="number" min={1} value={horizon} onChange={(e) => setHorizon(e.target.valueAsNumber || 1)} /></Field>
            <Field label="Max jobs/day"><Input type="number" min={1} value={maxPerDay} onChange={(e) => setMaxPerDay(e.target.valueAsNumber || 1)} /></Field>
            <Field label="Travel buffer (min)"><Input type="number" min={0} value={buffer} onChange={(e) => setBuffer(e.target.valueAsNumber || 0)} /></Field>
          </div>

          <button className={btnPrimary + ' mt-5'} onClick={persist} disabled={busy}>
            {busy ? 'Saving…' : 'Save booking settings'}
          </button>
        </div>
      </Card>

      <Card>
        <CardHeader title="Bookable services" count={services.data?.filter((s) => s.active).length ?? 0} />
        <div className="px-5 pb-5">
          {(services.data ?? []).filter((s) => s.active).length === 0 ? (
            <p className="text-[13px] text-token-secondary">
              No services yet. Customers can’t book until there’s at least one.
            </p>
          ) : (
            <ul className="divide-y divide-token-subtle">
              {(services.data ?? []).filter((s) => s.active).map((s) => (
                <li key={s.id} className="flex items-center justify-between gap-3 py-2.5">
                  <span className="min-w-0">
                    <span className="block truncate text-[14px] font-medium text-token-primary">{s.name}</span>
                    <span className="block text-[12px] text-token-secondary">{s.duration_min} min{s.price != null && ` · ${peso(s.price, { whole: true })}`}</span>
                  </span>
                  <button className={btnSecondary} onClick={() => removeService(s.id)}>Remove</button>
                </li>
              ))}
            </ul>
          )}

          <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_120px_120px_auto]">
            <Field label="Service"><Input value={svcName} onChange={(e) => setSvcName(e.target.value)} placeholder="Blocked drain" /></Field>
            <Field label="Minutes"><Input type="number" min={15} step={15} value={svcMins} onChange={(e) => setSvcMins(e.target.valueAsNumber || 60)} /></Field>
            <Field label="Price"><Input type="number" min={0} value={svcPrice} onChange={(e) => setSvcPrice(e.target.value)} placeholder="optional" /></Field>
            <div className="flex items-end"><button className={btnSecondary} onClick={addService}>Add</button></div>
          </div>
        </div>
      </Card>
    </div>
  )
}

/**
 * Safety & SWMS (spec 035 US1). The whole feature is dormant until an owner ticks something here,
 * and that is deliberate: this ships into tenants doing carpet cleaning and glazing whose work is
 * not high-risk construction work at all, and whose job pages must not change by a pixel.
 */
function SafetyTab() {
  const { company, save } = useCompany()
  const [cats, setCats] = useState<string[] | null>(null)
  const [enforcement, setEnforcement] = useState<'warn' | 'block'>('warn')
  const [healthMode, setHealthMode] = useState<'warn' | 'block'>('warn')
  const [note, setNote] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!company) return
    setCats((c) => c ?? company.hrcw_categories ?? [])
    setEnforcement(company.swms_enforcement ?? 'warn')
    setHealthMode(company.health_check_mode ?? 'warn')
  }, [company])

  const j = jurisdictionFor(company?.state)
  const list = hrcwList(j)
  const selected = cats ?? []

  // Victoria never harmonised, so the list a Victorian owner ticks is a different list. Moving
  // between them is handled rather than ignored — see mapCategories(), which folds `tunnel` into
  // trench-or-shaft instead of dropping it silently.
  useEffect(() => {
    if (!company || cats === null) return
    const stray = cats.filter((k) => !list.some((c) => c.key === k))
    if (!stray.length) return
    const { keys, note: n } = mapCategories(cats, j === 'vic' ? 'harmonised' : 'vic', j)
    setCats(keys)
    setNote(n)
  }, [j])

  const persist = async (next: { cats?: string[]; enforcement?: 'warn' | 'block'; healthMode?: 'warn' | 'block' }) => {
    setBusy(true)
    try {
      await save.mutateAsync({
        hrcw_categories: next.cats ?? selected,
        swms_enforcement: next.enforcement ?? enforcement,
        health_check_mode: next.healthMode ?? healthMode,
      })
      toast.success('Saved')
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Could not save') }
    finally { setBusy(false) }
  }

  return (
    <div className="space-y-5">
      <Card className="p-[22px]">
        <p className="font-display text-[15px] font-bold text-token-primary">Does your work involve high-risk construction work?</p>
        <p className="mt-1 text-[13px] leading-relaxed text-token-secondary">
          If it does, the law requires a Safe Work Method Statement prepared before the work starts, with the crew
          consulted. Tick what your work routinely involves and every new job will arrive flagged, with a template
          ready. Tick nothing and nothing in MySerbisyo changes.
        </p>
        <p className="mt-2 text-[12.5px] text-token-muted">
          Showing the list for the {JURISDICTION_LABEL[j]}
          {j === 'vic' ? ' — Victoria has 19 activities and did not harmonise with the other states.' : ' — 18 activities.'}
          {' '}Change your state in Business profile if this is wrong.
        </p>

        {note && (
          <p className="mt-3 rounded-lg border border-token-warning bg-token-warning-surface p-3 text-[12.5px] leading-relaxed text-token-warning">{note}</p>
        )}

        <div className="mt-4">
          <HrcwPicker jurisdiction={j} selected={selected} onChange={setCats} />
        </div>
        <button className={btnPrimary + ' mt-4'} disabled={busy} onClick={() => persist({})}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </Card>

      {selected.length > 0 && (
        <Card className="p-[22px]">
          <p className="font-display text-[15px] font-bold text-token-primary">Starting a job without a signed SWMS</p>
          <p className="mt-1 text-[13px] leading-relaxed text-token-secondary">
            Either way it is recorded, and either way the person is one tap from the SWMS. How hard the product pushes
            is your call, not ours.
          </p>
          <div className="mt-3 space-y-2">
            {([
              ['warn', 'Warn — ask for a reason, then let them start'],
              ['block', 'Block — only an admin can override, with a reason'],
            ] as const).map(([v, lbl]) => (
              <label key={v} className="flex cursor-pointer items-start gap-2 text-[13.5px] text-token-primary">
                <input type="radio" className="mt-[3px]" checked={enforcement === v} onChange={() => { setEnforcement(v); void persist({ enforcement: v }) }} />
                <span>{lbl}</span>
              </label>
            ))}
          </div>
        </Card>
      )}

      {/* 048 T036 — the health-check gate. It reads the SAME hrcw_categories above rather than
          adding a parallel tagging feature: one list of what your work involves, two mechanisms
          hanging off it. */}
      <Card className="p-[22px]">
        <p className="font-display text-[15px] font-bold text-token-primary">Starting work without answering the health check</p>
        <p className="mt-1 text-[13px] leading-relaxed text-token-secondary">
          A workflow can send a fitness-for-work form to whoever is on the job. This setting decides what happens when
          somebody clocks on without answering it. Nothing happens at all until you build that workflow, so this is
          safe to set now.
        </p>
        <div className="mt-3 space-y-2">
          {([
            ['warn', 'Warn — they can start, and the office is told straight away'],
            ['block', 'Block — the clock-on is refused until the form is answered'],
          ] as const).map(([v, lbl]) => (
            <label key={v} className="flex cursor-pointer items-start gap-2 text-[13.5px] text-token-primary">
              <input
                type="radio" className="mt-[3px]" name="health-mode"
                checked={healthMode === v}
                onChange={() => { setHealthMode(v); void persist({ healthMode: v }) }}
              />
              <span>{lbl}</span>
            </label>
          ))}
        </div>
        <p className="mt-3 text-[12.5px] leading-relaxed text-token-muted">
          Block is enforced by the database, not by the app. An old phone build cannot walk around it, and neither can
          moving the job to In-Progress by hand.
        </p>
      </Card>

      <Card className="p-[22px]">
        <p className="font-display text-[15px] font-bold text-token-primary">A note on what this is and is not</p>
        <p className="mt-1 text-[13px] leading-relaxed text-token-secondary">
          The templates are a starting point built from the regulators' own guidance. They are not legal advice, and
          using them does not by itself make you compliant — the duty to prepare a SWMS that fits the actual site and
          the actual crew rests with your business. Adapt every one of them on site, and delete anything that does not
          apply there.
        </p>
      </Card>
    </div>
  )
}
